import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SiteEntity } from '../models';
import * as lz from 'lz-string';

@Injectable({
    providedIn: 'root'
})
export class GlobalStateService {
    private readonly SITE_KEY = 'current_site';
    private readonly LANG_KEY = 'current_lang';

    // Default language
    private readonly DEFAULT_LANG = 'en';

    // State Subjects
    private currentSiteSubject = new BehaviorSubject<SiteEntity | null>(null);
    private currentLanguageSubject = new BehaviorSubject<string>(this.DEFAULT_LANG);

    // Public Observables
    currentSite$ = this.currentSiteSubject.asObservable();
    currentLanguage$ = this.currentLanguageSubject.asObservable();

    constructor() {
        this.init();
    }

    private init() {
        // Load Site
        const storedSite = localStorage.getItem(this.SITE_KEY);
        if (storedSite) {
            try {
                // Try decompressing first
                let parsedSite = JSON.parse(lz.decompressFromUTF16(storedSite) || '');
                // Fallback for plain JSON if decompression fails
                if (!parsedSite) {
                    parsedSite = JSON.parse(storedSite);
                }
                this.currentSiteSubject.next(parsedSite);
            } catch (e) {
                console.warn('Failed to parse stored site', e);
            }
        }

        // Load Language
        const storedLang = localStorage.getItem(this.LANG_KEY);
        if (storedLang) {
            this.currentLanguageSubject.next(storedLang);
        }
    }

    setSite(site: SiteEntity) {
        this.currentSiteSubject.next(site);
        // Persist compressed to match existing patterns
        localStorage.setItem(this.SITE_KEY, lz.compressToUTF16(JSON.stringify(site)));
        // Sync legacy key for components that might still look for it directly
        if (site && site.uniq) {
            localStorage.setItem('selectedSiteUniq', site.uniq);
        }
    }

    setLanguage(lang: string) {
        this.currentLanguageSubject.next(lang);
        localStorage.setItem(this.LANG_KEY, lang);
    }

    // Helper to get current value synchronously (use sparingly)
    getCurrentSite(): SiteEntity | null {
        return this.currentSiteSubject.value;
    }

    getCurrentLanguage(): string {
        return this.currentLanguageSubject.value;
    }
}
